import {
    BuildingOffice2Icon,
    CalendarIcon,
    ChartBarIcon,
    ClipboardDocumentCheckIcon,
    Cog8ToothIcon,
    FolderIcon,
    UserIcon,
    UsersIcon,
} from "@heroicons/react/24/outline";
import { HandshakeIcon } from "components/ui/handshake-icon";
import React from "react";

export interface NavItem {
    label: string;
    url: string;
    visible?: boolean;
    activeMode?: "exact" | "contains";
}

export interface NavSection {
    id: string;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    url: string;
    expandable: boolean;
    items: NavItem[];
    isActive: (pathname: string) => boolean;
    visible?: boolean;
}

interface NavigationConfig {
    showReferrals: boolean;
    legacyReportsEnabled: boolean;
    viewAllEnabled: boolean;
    viewOwnEnabled: boolean;
    viewCustomReportsEnabled: boolean;
    referralSourceReportEnabled: boolean;
    stageConversionEnabled: boolean;
    showAgencySettings: boolean;
    showRegionSettings: boolean;
    manageWorkflowsAllowed: boolean;
    manageUsersAllowed: boolean;
    manageIntegrationsAllowed: boolean;
    franchiseManagementEnabled: boolean;
    franchiseRoyaltyReportEnabled: boolean;
    csvDownloadEnabled: boolean;
    homeCarePrototypeEnabled: boolean;
    outstandingInvoicesReportEnabled: boolean;
    placementEnabled: boolean;
}

const isClientsPath = (pathname: string): boolean => {
    return (
        pathname === "/" ||
        pathname === "/pipeline" ||
        pathname === "/timeline" ||
        pathname === "/transfers" ||
        pathname === "/home-care-pipeline" ||
        pathname.startsWith("/clients") ||
        pathname.startsWith("/home-care-clients")
    );
};

export function getNavigationSections(config: NavigationConfig): NavSection[] {
    return [
        {
            id: "clients",
            label: "Clients",
            icon: UsersIcon,
            url: config.placementEnabled ? "/pipeline" : "/home-care-pipeline",
            expandable: true,
            isActive: isClientsPath,
            visible: config.placementEnabled || config.homeCarePrototypeEnabled,
            items: [
                {
                    label: config.homeCarePrototypeEnabled
                        ? "Placement Pipeline"
                        : "Pipeline",
                    url: "/pipeline",
                    visible: config.placementEnabled,
                },
                {
                    label: "Home Care Pipeline",
                    url: "/home-care-pipeline",
                    visible: config.homeCarePrototypeEnabled,
                },
                {
                    label: "Timeline",
                    url: "/timeline",
                    visible: config.placementEnabled,
                },
                {
                    label: "All Clients",
                    url: "/clients",
                    visible: config.placementEnabled,
                },
                {
                    label: "Transfers",
                    url: "/transfers",
                    visible: config.placementEnabled,
                },
            ],
        },
        {
            id: "communities",
            label: "Communities",
            icon: BuildingOffice2Icon,
            url: "/communities",
            expandable: false,
            isActive: pathname => pathname.startsWith("/communities"),
            items: [],
            visible: config.placementEnabled,
        },
        {
            id: "referrals",
            label: "Referrals",
            icon: HandshakeIcon,
            url: "/references",
            expandable: false,
            isActive: pathname => pathname.startsWith("/references"),
            items: [],
            visible: config.showReferrals,
        },
        {
            id: "tasks",
            label: "Tasks",
            icon: ClipboardDocumentCheckIcon,
            url: "/tasks",
            expandable: true,
            isActive: pathname => pathname.startsWith("/tasks"),
            items: [
                { label: "Active Tasks", url: "/tasks" },
                {
                    label: "Records Without Tasks",
                    url: "/tasks/missing",
                },
            ],
        },
        {
            id: "calendar",
            label: "Calendar",
            icon: CalendarIcon,
            url: "/calendar",
            expandable: false,
            isActive: pathname => pathname.startsWith("/calendar"),
            items: [],
        },
        {
            id: "files",
            label: "Files",
            icon: FolderIcon,
            url: "/files",
            expandable: true,
            isActive: pathname => pathname.startsWith("/files"),
            items: [
                { label: "Our Files", url: "/files" },
                { label: "SignWise", url: "/files/signwise" },
            ],
        },
        {
            id: "reports",
            label: "Reports",
            icon: ChartBarIcon,
            url: config.placementEnabled ? "/reports" : "/reports/show/custom",
            expandable: true,
            isActive: pathname => pathname.startsWith("/reports"),
            items: [
                {
                    label: "Overview",
                    url: "/reports",
                    visible: config.placementEnabled,
                },
                {
                    label: "Client Placement",
                    url: "/reports/show/revenue",
                    visible:
                        config.placementEnabled &&
                        config.legacyReportsEnabled &&
                        (config.viewAllEnabled || config.viewOwnEnabled),
                },
                {
                    label: "Referrals",
                    url: "/reports/show/referrals",
                    visible:
                        config.placementEnabled &&
                        config.legacyReportsEnabled &&
                        !config.referralSourceReportEnabled,
                },
                {
                    label: "Referral Source",
                    url: "/reports/show/referral-source",
                    visible:
                        config.referralSourceReportEnabled && config.placementEnabled,
                },
                {
                    label: "Custom Reports",
                    url: "/reports/show/custom",
                    visible: config.viewCustomReportsEnabled,
                    activeMode: "contains",
                },
                {
                    label: "Stage Conversion",
                    url: "/reports/show/stage-conversion",
                    visible: config.stageConversionEnabled,
                },
                {
                    label: "Franchise Royalties",
                    url: "/reports/show/franchise-royalties",
                    visible: config.franchiseRoyaltyReportEnabled,
                },
                {
                    label: "Outstanding Invoices",
                    url: "/reports/show/outstanding-invoices",
                    visible: config.outstandingInvoicesReportEnabled,
                },
            ],
        },
        {
            id: "my-settings",
            label: "My Settings",
            icon: UserIcon,
            url: "/settings/account",
            expandable: true,
            isActive: pathname =>
                pathname.startsWith("/settings/account") ||
                pathname.startsWith("/settings/templates"),
            items: [
                {
                    label: "My settings",
                    url: "/settings/account",
                    activeMode: "contains",
                },
                {
                    label: "Email Templates & Signatures",
                    url: "/settings/templates",
                    activeMode: "contains",
                },
            ],
        },
        {
            id: "agency-settings",
            label: "Agency Settings",
            icon: Cog8ToothIcon,
            url: "/settings/agency",
            expandable: true,
            isActive: pathname => pathname.startsWith("/settings"),
            items: [
                {
                    label: "Agency settings",
                    url: "/settings/agency",
                    visible: config.showAgencySettings,
                    activeMode: "contains",
                },
                {
                    label: "Office settings",
                    url: "/settings/region",
                    visible: config.showRegionSettings,
                    activeMode: "contains",
                },
                {
                    label: "Workflows",
                    url: "/settings/workflow",
                    visible: config.manageWorkflowsAllowed,
                    activeMode: "contains",
                },
                {
                    label: "User accounts",
                    url: "/settings/users",
                    visible: config.manageUsersAllowed,
                    activeMode: "contains",
                },
                {
                    label: "Integrations",
                    url: "/settings/integrations",
                    visible: config.manageIntegrationsAllowed,
                    activeMode: "contains",
                },
                {
                    label: "Franchise Management",
                    url: "/settings/franchise",
                    visible: config.franchiseManagementEnabled,
                    activeMode: "contains",
                },
            ],
        },
    ];
}
